// pages/policy.js — Policy page.
//
// Owns the `data-page="policy"` section. Renders the policy rule
// list (drag-drop reorderable, filterable by text + decision chips),
// recent-firings table (24h aggregate of policy.deny/audit_deny
// events), and discovered-tools list. The visual rule builder
// modal that fires from the "+ Create rule" button stays bound in
// dashboard.js — modal lifecycle isn't part of the page module
// surface.
//
// Migration note: only renderPolicyPage (the top-level) moves here.
// The 8 sub-renderers (renderPolicyRuleRow, applyPolicyListFilter,
// renderPolicyFilterBar, renderPolicyRecentFirings,
// renderDiscoveredTools, wirePolicyRuleDragDrop, fetchPolicy)
// stay in dashboard.js for now — each has its own complex
// dependency surface. They migrate in Phase E as part of the
// visual rule builder consolidation.
//
// Per Worktrees bug-sweep finding: no initial render in start() —
// legacy code handles activePage-change render.
import { state } from "../state.js";
import { policyState } from "../page-state.js";
import { patchHTML } from "../dom.js";
import { escapeHTML } from "../util.js";
import { fetchPolicy } from "../fetchers.js";
import {
  applyPolicyListFilter,
  renderPolicyFilterBar,
  renderPolicyRuleRow,
  renderPolicyRecentFirings,
  renderDiscoveredTools,
  wirePolicyRuleDragDrop,
  setPolicyReorderCallback,
} from "../policy.js";

// Register the post-reorder render callback. The policy.js drag
// handlers need to re-render after a drop, but can't import
// renderPolicyPage directly (would be a circular import). This
// callback bridges the gap.
setPolicyReorderCallback(() => renderPolicyPage());

export async function renderPolicyPage() {

  // Both local and cloud modes use the same /v1/rules-shaped
  // endpoints + the same visual rule builder. Cloud writes to
  // team_policy via the coordinator Worker; local writes to
  // .agentcollision/policy.yaml via the daemon. Same UI; the
  // dashboard doesn't need to know the difference.
  if (!policyState.data && !policyState.error) await fetchPolicy();
  const body = document.getElementById("policy-body");
  if (!body) return;
  if (policyState.error) {
    patchHTML(
      body,
      '<div class="empty"><div class="empty-title">Could not load policy</div><div class="empty-detail">' +
        escapeHTML(policyState.error) +
        "</div></div>",
    );
    return;
  }
  const p = policyState.data;
  if (!p) return;
  // Rules the daemon/coordinator can't apply (invalid shape, or options
  // that never match). Everything else still applies.
  const skipped = p.skipped || [];
  const skippedBanner = skipped.length
    ? '<div style="margin-bottom: 16px; padding: 10px 14px; background: var(--warning-bg); border: 1px solid var(--warning); border-radius: var(--r-md); font-size: 12.5px; color: var(--text);"><strong>' +
      skipped.length +
      " rule(s) are not applied</strong> until fixed:<ul style=\"margin: 6px 0 0 18px; padding: 0;\">" +
      skipped.map((m) => "<li>" + escapeHTML(m) + "</li>").join("") +
      "</ul></div>"
    : "";
  const noRules = !p.rules || p.rules.length === 0;
  if (noRules) {
    patchHTML(
      body,
      skippedBanner +
      `<div class="empty">
        <div class="empty-title">No policy gating active</div>
        <div class="empty-detail">
          Create <code>.agentcollision/policy.yaml</code> in your repo to enable gates.<br/>
          Easiest path: install a curated template with
          <pre style="text-align:left; margin-top:8px; padding:8px; background:var(--bg-soft); border-radius:6px; font-size:12px;">agc policy templates init amazon-mandate</pre>
        </div>
      </div>`,
    );
    return;
  }
  let html = skippedBanner;

  // Quiet meta strip — only renders when there's something
  // non-default to report. observe_only is the only flag that
  // changes evaluation behavior; break_glass + sources are operator-
  // config noise the user doesn't need on this page.
  const d = p.defaults || {};
  if (d.observe_only) {
    html +=
      '<div style="margin-bottom: 16px; padding: 10px 14px; background: var(--warning-bg); border: 1px solid var(--warning); border-radius: var(--r-md); font-size: 12.5px; color: var(--text);">';
    html +=
      '<strong>Observe-only mode is on.</strong> Every matching rule downgrades to allow + logs a "would have blocked" event. Flip in policy.yaml when ready to enforce.';
    html += "</div>";
  }

  // Rules — the headline content. No "Rules (2)" label, no SOURCES
  // list, no DEFAULTS dump. Just the rules in a clean stacked list.
  if (!p.rules || p.rules.length === 0) {
    html +=
      '<div class="card" style="padding: 32px; text-align: center; color: var(--text-secondary);">No rules yet. Click <strong>+ Create rule</strong> above to add one.</div>';
  } else {
    // Apply filters: text search + decision chips. State lives on
    // policyListFilter (declared near policyState) and persists
    // across re-renders.
    const filteredRules = applyPolicyListFilter(p.rules);
    // Filter bar — search input + decision chips.
    html += renderPolicyFilterBar(p.rules, filteredRules);
    // Order caption — rules evaluate top-down, first match wins.
    html +=
      '<div style="font-size: 12px; color: var(--text-secondary); margin-bottom: 10px; display: flex; align-items: center; gap: 6px;">' +
      '<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="opacity: 0.7;"><polyline points="6 9 12 15 18 9"/></svg>' +
      '<span><strong>Evaluated in order, first match wins.</strong> Drag the <span style="opacity: 0.6;">⋮⋮</span> handle to reorder.</span>' +
      "</div>";
    if (filteredRules.length === 0) {
      html +=
        '<div class="card" style="padding: 24px; text-align: center; color: var(--text-secondary); font-size: 13px;">No rules match the current filter. <button class="btn-pick" type="button" id="policy-filter-clear" style="margin-left: 8px;">Clear filter</button></div>';
    } else {
      // Container for the rows; drag-drop hooks bind on
      // .policy-rule-row instead of card now.
      html +=
        '<div class="policy-rule-list" style="border: 1px solid var(--border); border-radius: var(--r-md); overflow: hidden;">';
      for (const r of filteredRules) html += renderPolicyRuleRow(r);
      html += "</div>";
    }
  }

  // Recent activity — surfaces policy.deny / audit_deny / confirm
  // events grouped by rule. Lets the user see whether their rules
  // are actually firing, and which audit-mode rules would have
  // blocked something so they know when to flip to enforce.
  html += '<div style="margin-top: 32px;">';
  html +=
    '<div style="display: flex; align-items: baseline; justify-content: space-between; margin-bottom: 8px;">';
  html +=
    '<div style="font-size: 11px; text-transform: uppercase; color: var(--text-tertiary); letter-spacing: 0.08em; font-weight: 600;">Recent activity</div>';
  html +=
    '<div style="font-size: 12px; color: var(--text-tertiary);">last 24h</div>';
  html += "</div>";
  html +=
    '<div style="font-size: 12.5px; color: var(--text-secondary); margin-bottom: 12px;">Which rules fired recently, and how often. Audit-mode rules log here without blocking — useful for deciding when to flip to enforce.</div>';
  html +=
    '<div id="policy-recent-firings"><div style="color: var(--text-tertiary); font-size: 13px;">Loading recent firings…</div></div>';
  html += "</div>";

  // Discovered tools — moved BELOW rules so the page leads with the
  // user's authored content, not with telemetry. Async-populated.
  html += '<div style="margin-top: 32px;">';
  html +=
    '<div style="font-size: 11px; text-transform: uppercase; color: var(--text-tertiary); letter-spacing: 0.08em; font-weight: 600; margin-bottom: 8px;">Discovered tools</div>';
  html +=
    '<div style="font-size: 12.5px; color: var(--text-secondary); margin-bottom: 12px;">Tools your agents have actually called recently. Click <strong>+ Rule</strong> on a row to govern it.</div>';
  html +=
    '<div id="policy-discovered-tools"><div style="color: var(--text-tertiary); font-size: 13px;">Aggregating recent events…</div></div>';
  html += "</div>";

  patchHTML(body, html);
  // Kick off the recent-firings + discovered-tools fetches after the
  // main render so neither blocks the user seeing rules.
  renderPolicyRecentFirings().catch(() => {});
  renderDiscoveredTools().catch(() => {});
  wirePolicyRuleDragDrop();
}

// ---- mount ----

let started = false;
let tickHandler = null;

function start() {
  if (started) return;
  started = true;
  // Initial render fires immediately on activation. The earlier
  // version deferred the first render to the next dashboard:tick,
  // which is fine on local-daemon mode where the tick loop fires
  // every few seconds — but cloud-pages mode has no daemon to
  // drive the tick loop, so the page sat on its skeleton forever
  // until the user navigated away and back. Mirrors the worktrees.js
  // fix from 2026-05-14.
  renderPolicyPage();
  tickHandler = () => {
    if (state.activePage === "policy") renderPolicyPage();
  };
  document.addEventListener("dashboard:tick", tickHandler);
}

function stop() {
  if (!started) return;
  started = false;
  if (tickHandler) {
    document.removeEventListener("dashboard:tick", tickHandler);
    tickHandler = null;
  }
}

function bindLifecycle() {
  const section = document.querySelector('section.page[data-page="policy"]');
  if (!section) return;
  if (section.classList.contains("active")) start();
  const obs = new MutationObserver((mutations) => {
    mutations.forEach((m) => {
      if (m.attributeName !== "class") return;
      if (section.classList.contains("active")) start();
      else stop();
    });
  });
  obs.observe(section, { attributes: true });
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", bindLifecycle);
} else {
  bindLifecycle();
}
